import React from "react";
import { Upload, Loader2, Landmark, CheckCircle2, X } from "lucide-react";

export const STATUS_PILL = {
  pending_review: { cls: "bg-amber-100 text-amber-800", label: "Under review" },
  approved: { cls: "bg-emerald-100 text-emerald-800", label: "Approved" },
  rejected: { cls: "bg-rose-100 text-rose-800", label: "Rejected — resubmit" },
  removing: { cls: "bg-slate-100 text-slate-600", label: "Removing…" },
};

export function KycDocumentRow({ def, doc, busy, digilockerSupported, onFile, onDigiLocker, onRemove }) {
  const { key, label, icon: Icon, hint } = def;
  const pill = doc ? STATUS_PILL[doc.status] : null;
  const approved = doc?.status === "approved";
  const fromDigiLocker = doc?.source === "digilocker";
  const isManual = doc?.source === "manual";
  const active = doc && doc.status !== "rejected" && doc.status !== "removing";
  const uploading = busy === `upload-${key}`;
  const fetching = busy === `digilocker-${key}`;
  const removing = busy === `remove-${key}`;
  // A manual document that is pending, rejected, or being removed blocks a new upload.
  const manualBlocksUpload = isManual && ["pending_review", "rejected", "removing"].includes(doc?.status);
    const removable = isManual && ["pending_review", "rejected", "removing"].includes(doc?.status);

  const handleRemove = () => {
    if (!window.confirm(`Remove ${doc.filename || label}? This cannot be undone.`)) return;
    onRemove(key, doc);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid={`kyc-doc-${key}`}>
      <div className="h-10 w-10 rounded-xl bg-[#FBF4DF] text-[#0A2540] grid place-items-center shrink-0">
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-[200px]">
        <p className="font-bold text-[#0A2540]">{label}</p>
        <p className="text-xs text-slate-500">{hint}</p>
        {isManual && doc?.filename && (
          <p className="mt-1 text-xs font-bold text-rose-600 inline-flex items-center gap-2" data-testid={`kyc-filename-${key}`}>
            {doc.filename}
            {removable && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                data-testid={`kyc-remove-${key}`}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                title="Remove uploaded document"
              >
                {removing ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
                <span className="text-[10px] uppercase tracking-wider">{removing ? "Removing" : "Remove"}</span>
              </button>
            )}
          </p>
        )}
        {fromDigiLocker && (
          <p className="mt-1 text-xs text-slate-600 inline-flex items-center gap-1" data-testid={`kyc-digilocker-provenance-${key}`}>
            <Landmark size={11} className="text-[#0A2540]" />
            Fetched from DigiLocker{doc.digilocker?.fetched_at ? ` on ${doc.digilocker.fetched_at.slice(0, 10)}` : ""}
            {doc.digilocker?.issuer ? ` · ${doc.digilocker.issuer}` : ""}
          </p>
        )}
        {doc?.review_note && <p className="mt-1 text-xs text-rose-600">Reviewer note: {doc.review_note}</p>}
      </div>
      {fromDigiLocker && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#0A2540] text-[#D4AF37]" data-testid={`kyc-source-${key}`}>
          <Landmark size={10} /> DigiLocker
        </span>
      )}
      {pill && (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pill.cls}`} data-testid={`kyc-status-${key}`}>
          {pill.label}
        </span>
      )}
      {approved ? (
        <span className="px-3 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-500 inline-flex items-center gap-1" data-testid={`kyc-done-${key}`}>
          <CheckCircle2 size={12} /> {fromDigiLocker ? "Fetched & approved" : "Approved"}
        </span>
      ) : active && fromDigiLocker ? (
        <span className="px-3 py-2 rounded-xl text-xs font-bold bg-[#FBF4DF] text-[#0A2540] inline-flex items-center gap-1" data-testid={`kyc-fetched-${key}`}>
          <CheckCircle2 size={12} /> Fetched — no upload needed
        </span>
      ) : (
        <div className="flex flex-wrap gap-2">
          <label className={`px-3 py-2 rounded-xl text-xs font-bold cursor-pointer inline-flex items-center ${active ? "bg-white border border-slate-200 text-[#0A2540] hover:border-[#0A2540]" : "bg-[#0A2540] text-[#D4AF37] hover:opacity-90"} ${manualBlocksUpload || removing ? "opacity-60" : ""}`}>
            {uploading ? <Loader2 size={14} className="animate-spin inline mr-1" /> : <Upload size={12} className="inline mr-1" />}
            {doc?.status === "removing" ? "Removing…" : active ? "Re-upload" : "Upload"}
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid={`kyc-upload-${key}`}
              onChange={(e) => onFile(key, e.target.files?.[0])}
              disabled={uploading || fetching || manualBlocksUpload || removing}
            />
          </label>
          {digilockerSupported && (
            <button
              type="button"
              onClick={() => onDigiLocker(key)}
              disabled={uploading || fetching || removing}
              data-testid={`kyc-digilocker-${key}`}
              className="px-3 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1 bg-[#0A2540] text-[#D4AF37] hover:opacity-90 disabled:opacity-60"
            >
              {fetching ? <Loader2 size={14} className="animate-spin" /> : <Landmark size={12} />}
              Fetch from DigiLocker
            </button>
          )}
        </div>
      )}
    </div>
  );
}
